import {defineStore} from 'pinia'
import { useAuthStore as auth } from '../auth/store'

const url = {
    GET_FORM_DATA: '/dashboard/v2/form/{pica_service_id}/user/configuration/',
    POST_RESPONSE: '/dashboard/v2/form/{pica_service_id}/user/response/',
}

export const useFormStore = defineStore('formStore', {
    state: () => ({
        data: null,
        id: null,
        picaServiceId: null,
        result: {},
        backUri: null,
        externalData: null,
        lang: 'it', // Default language
    }),
    getters: {
        form: (state) => state.data,
        formData: (state) => ({ form: state.data }),
    },
    actions: {
        // any amount of arguments, return a promise or not
        async fetchForm(picaServiceId) {
            if (this.picaServiceId === picaServiceId) return

            const response = await auth().client.get(url.GET_FORM_DATA.replace('{pica_service_id}', picaServiceId))
            this.data = response.data.form
            this.picaServiceId = picaServiceId
        },
        async postResponse(data) {
            // Add system fields to the response
            const browserLanguage = navigator.language || navigator.userLanguage || ''
            const enrichedData = {
                ...data,
                user_id: this.externalData?.user_id || '',
                browser_language: browserLanguage,
                browser_language_iso: browserLanguage.split('-')[0].toLowerCase()
            }
            await auth().client.post(url.POST_RESPONSE.replace('{pica_service_id}', this.picaServiceId), {
                response: enrichedData,
            })
        },
        async redirectAfterComplete() {
            window.location.replace(this.backUri);
        },
        setBackUri(uri) {
            this.backUri = uri;
        },
        setExternalData(data) {
            this.externalData = data;
        },
        setLang(lang) {
            this.lang = lang;
        },
        updateResult(fieldName, value) {
            this.result[fieldName] = value;
        },
        initializeForm(initialData = {}) {
            if (!this.data) return;
            
            let fields = {};
            if (this.data.fields) {
                this.data.fields.forEach(field => {
                    if (field.type === 'multySelect') {
                        fields[field.key] = initialData[field.key] || [];
                    } else {
                        fields[field.key] = initialData[field.key] || '';
                    }
                });
            }
            if (this.data.consents) {
                this.data.consents.forEach(consent => {
                    fields[consent.key] = initialData[consent.key] || false;
                });
            }
            this.result = fields;
        },
        formIsValid() {
            if (!this.data) return false;
            
            const isValidForSubmit = (fieldType, mandatory, value) => {
                if (!mandatory && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
                    return true;
                }
                
                switch (fieldType) {
                    case 'email':
                        return /^[^@]+@[^@]+\.[^@]+$/.test(value);
                    case 'text':
                        return value && value.length > 0;
                    case 'phone':
                        return /^\+?[0-9]+$/.test(value) && value.length >= 2;
                    case 'select':
                        return value && value !== '';
                    case 'multySelect':
                        return Array.isArray(value) && value.length > 0;
                    case 'consent':
                        return value === true;
                    case 'date':
                        if (value.length <= 0) return false;
                        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                        if (!dateRegex.test(value)) return false;
                        const date = new Date(value);
                        return date instanceof Date && !isNaN(date) && value === date.toISOString().split('T')[0];
                    default:
                        return true;
                }
            };
            
            if (this.data.fields) {
                for (const field of this.data.fields) {
                    if (!isValidForSubmit(field.type, field.mandatory, this.result[field.key])) {
                        return false;
                    }
                }
            }
            
            if (this.data.consents) {
                for (const consent of this.data.consents) {
                    if (!isValidForSubmit('consent', consent.mandatory, this.result[consent.key])) {
                        return false;
                    }
                }
            }
            
            return true;
        },
        getValidationProblems() {
            return {};
        }
    }
})